import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from './Icon';
import { apiService } from '@/services/api';

/**
 * Sidebar account area, per the Magni design system: a user row + a company
 * (client) row, each opening a popover — the company row is the client indicator
 * and switcher. Tone-coloured avatars, name + "NIF {taxId}". Switching calls the
 * auth server (POST /clients/switch) and, on success, drives a full OIDC re-login
 * so the new active-client claim surfaces in the session.
 */
interface ClientItem { id: number; name: string; taxId?: string | null; }

const TONES = ['red', 'blue', 'green', 'gray'] as const;
const toneFor = (id: number) => TONES[Math.abs(id) % TONES.length];
// First letter/digit of each word, so punctuation in legal names ("Demo ( Dispositivo…")
// doesn't end up in the avatar.
const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .map((w) => w.match(/[\p{L}\p{N}]/u)?.[0])
    .filter(Boolean)
    .join('')
    .toUpperCase()
    .slice(0, 2) || '–';

export default function AccountMenu({
  collapsed,
  displayName,
  displayEmail,
  initials,
  onLogout,
}: {
  collapsed: boolean;
  displayName: string;
  displayEmail: string;
  initials: string;
  onLogout: () => void;
}) {
  const [open, setOpen] = useState<null | 'user' | 'company'>(null);
  const [clients, setClients] = useState<ClientItem[]>([]);
  // The active client is authoritative from the server (user.clientId_currently_logged),
  // not a browser value — it only changes via a switch + re-login.
  const [currentClientId, setCurrentClientId] = useState<number | null>(null);
  const [switching, setSwitching] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentId = currentClientId != null ? String(currentClientId) : undefined;

  useEffect(() => {
    let cancelled = false;
    apiService
      .get<{ clients: ClientItem[]; currentClientId?: number | null }>('/clients')
      .then((res) => {
        if (cancelled || !res.value) return;
        if (res.value.clients) setClients(res.value.clients);
        setCurrentClientId(res.value.currentClientId ?? null);
      })
      .catch(() => { /* indicator falls back to id */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(null); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(null); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const current = clients.find((c) => String(c.id) === currentId);
  const clientName = current?.name || (currentId ? `Client #${currentId}` : '');
  const clientNif = current?.taxId || '';
  const tone = current ? toneFor(current.id) : 'gray';

  const switchTo = async (c: ClientItem) => {
    if (c.id === currentClientId || switching) { setOpen(null); return; }
    setSwitching(true);
    try {
      const res = await apiService.post<{ requiresReauth: boolean }>('/clients/switch', { clientId: c.id });
      // A successful switch always requires a fresh OIDC challenge for the new active-client claim
      // to surface (a background refresh re-issues the old principal). Full-page redirect to the
      // BFF login, returning to the current page; the auth-server SSO session makes it seamless.
      if (res.value?.requiresReauth) {
        window.location.href = `/api/auth/login?returnUrl=${encodeURIComponent(window.location.pathname)}`;
        return;
      }
      window.location.reload();
    } catch {
      // Switch rejected (e.g. not assigned to that client) — keep the menu open, stay put.
      setSwitching(false);
    }
  };

  return (
    <div className="acct" ref={ref}>
      <button
        className={`acct-row user ${open === 'user' ? 'active' : ''}`}
        onClick={() => setOpen((o) => (o === 'user' ? null : 'user'))}
        title="Account"
      >
        <div className="acct-avatar user-av">{initials}</div>
        {!collapsed && (
          <>
            <div className="acct-meta">
              <div className="acct-name">{displayName}</div>
              {displayEmail && <div className="acct-sub">{displayEmail}</div>}
            </div>
            <Icon name="chevron_right" size={14} className="acct-chev" />
          </>
        )}
      </button>

      {currentId && (
        <button
          className={`acct-row company ${open === 'company' ? 'active' : ''}`}
          onClick={() => setOpen((o) => (o === 'company' ? null : 'company'))}
          title="Client"
        >
          <div className={`acct-avatar co-av tone-${tone}`}>{current ? initialsOf(current.name) : '#'}</div>
          {!collapsed && (
            <>
              <div className="acct-meta">
                <div className="acct-name">{clientName}</div>
                {clientNif && <div className="acct-sub">NIF {clientNif}</div>}
              </div>
              <Icon name="chevron_right" size={14} className="acct-chev" />
            </>
          )}
        </button>
      )}

      {open === 'user' && (
        <div className="acct-pop" role="dialog" aria-label="Account">
          <div className="pop-head">
            <div className="acct-avatar user-av" style={{ width: 40, height: 40, borderRadius: 10, fontSize: 14 }}>{initials}</div>
            <div style={{ minWidth: 0 }}>
              <div className="pop-eyebrow">Signed in</div>
              <div className="pop-name">{displayName}</div>
              {displayEmail && <div className="pop-mail">{displayEmail}</div>}
            </div>
          </div>
          <div className="pop-foot">
            <Link className="pop-signout pop-link" to="/settings/notifications" onClick={() => setOpen(null)}>
              <Icon name="settings" size={14} />
              Notification settings
            </Link>
            <button className="pop-signout" onClick={onLogout}>
              <Icon name="logout" size={14} />
              Sign out
            </button>
          </div>
        </div>
      )}

      {open === 'company' && (
        <div className="acct-pop co" role="dialog" aria-label="Clients">
          <div className="co-pop-head">
            <div className="pop-eyebrow">{clients.length > 1 ? 'Switch client' : 'Client'}</div>
          </div>
          <div className="co-grid">
            {clients.map((c) => {
              const isCurrent = String(c.id) === currentId;
              return (
                <button
                  key={c.id}
                  className={`co-card ${isCurrent ? 'current' : ''}`}
                  onClick={() => switchTo(c)}
                  title={c.name}
                >
                  <div className={`acct-avatar co-av big tone-${toneFor(c.id)}`}>{initialsOf(c.name)}</div>
                  <div className="co-card-name">{c.name}</div>
                  {c.taxId && <div className="co-card-nif">NIF {c.taxId}</div>}
                  {isCurrent && <span className="co-current"><Icon name="check_circle" size={12} />Current</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
